'use client';

import type { PropsWithChildren } from 'react';
import { memo, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useFragment } from '@apollo/client';
import type { AssetAggregateLayoutEnum } from '@frameio/graphql-client/generated';
import type { FieldDefinitionInfoFragment } from '../gql/generated/FieldDefinitionInfo';
import { useLayoutFragment } from '../gql/generated/Layout';
import type { VisibleCardFieldsFragment } from '../gql/generated/VisibleCardFields';
import { VisibleCardFields } from '../gql/generated/VisibleCardFields';
import type { VisibleRowFieldsFragment } from '../gql/generated/VisibleRowFields';
import { VisibleRowFields } from '../gql/generated/VisibleRowFields';

type Value = {
  assetAggregate?: {
    __typename: string;
    id: string;
  };
  visibleFieldDefinitions?: FieldDefinitionInfoFragment[];
  layout: AssetAggregateLayoutEnum;
};

const AssetAggregateContext = createContext<Value>({
  assetAggregate: undefined,
  visibleFieldDefinitions: undefined,
  layout: 'GRID',
});

type Props = PropsWithChildren<Pick<Value, 'assetAggregate'>>;

export const AssetAggregateContextProvider = memo(
  ({ children, assetAggregate: assetAggregateFromProps }: Props) => {
    const assetAggregate = useMemo(
      () =>
        assetAggregateFromProps?.id && assetAggregateFromProps?.__typename
          ? {
              __typename: assetAggregateFromProps.__typename,
              id: assetAggregateFromProps.id,
            }
          : undefined,
      [assetAggregateFromProps?.id, assetAggregateFromProps?.__typename]
    );
    const layout =
      useLayoutFragment({
        from: assetAggregate ?? {},
      })?.data?.appearance?.layout ?? 'GRID';

    const { data: visibleFieldsData, complete: visibleFieldsComplete } =
      useFragment<VisibleCardFieldsFragment | VisibleRowFieldsFragment>({
        fragment: layout === 'GRID' ? VisibleCardFields : VisibleRowFields,
        fragmentName:
          layout === 'GRID' ? 'VisibleCardFields' : 'VisibleRowFields',
        from: assetAggregate || {},
      });

    const value = useMemo(
      () => ({
        assetAggregate,
        visibleFieldDefinitions: visibleFieldsComplete
          ? getVisibleFieldDefinitions(visibleFieldsData)
          : undefined,
        layout,
      }),
      [assetAggregate, visibleFieldsComplete, visibleFieldsData, layout]
    );

    return (
      <AssetAggregateContext.Provider value={value}>
        {children}
      </AssetAggregateContext.Provider>
    );
  }
);

export const useVisibleFieldDefinitions = () =>
  useContextSelector(
    AssetAggregateContext,
    (value) => value.visibleFieldDefinitions
  );

export const useAssetAggregateLayout = () =>
  useContextSelector(AssetAggregateContext, (value) => value.layout);

export const useBareAssetAggregate = () =>
  useContextSelector(AssetAggregateContext, (value) => value.assetAggregate);

const getVisibleFieldDefinitions = (
  data: VisibleCardFieldsFragment | VisibleRowFieldsFragment
) => {
  if ('fieldsCard' in data)
    return (
      data.fieldsCard?.nodes
        ?.filter((node) => node?.isVisible)
        .map((node) => node!.fieldDefinition!) ?? []
    );
  if ('fieldsList' in data)
    return (
      data.fieldsList?.nodes
        ?.filter((node) => node?.isVisible)
        .map((node) => node!.fieldDefinition!) ?? []
    );
  return [];
};
